import { AlarmClock, BellRing, Eye, Radar } from 'lucide-react'
import type { ChatMessage } from '@shared/types'
import { Markdown } from './Markdown'
import { ActivityTimeline } from './ActivityTimeline'
import { Citations } from './Citations'
import { WorkerCard } from '../workers/WorkerCard'
import { OrderCard } from '../workers/OrderCard'
import { WorkerGroupCard } from '../workers/WorkerGroupCard'
import { WatcherCard } from '../workers/WatcherCard'

/** Worker / order drafts this turn produced — each gets its system-computed approval card. */
function drafted(message: ChatMessage, tool: 'worker_draft' | 'worker_group_draft' | 'order_draft' | 'watch_create' | 'scan_create', key: 'workerId' | 'groupId' | 'ticketId' | 'watcherId' | 'scanId'): string[] {
  const ids: string[] = []
  for (const e of message.toolEvents ?? []) {
    const id = e.name === tool && e.status === 'done' ? (e.output as Record<string, unknown> | undefined)?.[key] : null
    if (typeof id === 'string' && !ids.includes(id)) ids.push(id)
  }
  return ids
}

/** Which xAI account answered, when the user has a Grok subscription connected. */
function BillingNote({ message, streaming }: { message: ChatMessage; streaming?: boolean }) {
  const live = streaming ? (message.meta as { notice?: string } | undefined)?.notice : undefined
  const b = message.meta?.billing
  const text = live
    ? live
    : !b || streaming
      ? null
      : b.via === 'subscription'
        ? 'Answered on your Grok subscription'
        : b.via === 'mixed'
          ? `Started on your Grok subscription, finished on the API key${b.switched ? ` — ${b.switched}` : ''}`
          : `Answered on the API key${b.switched ? ` — ${b.switched}` : ''}`
  if (!text) return null
  return <div className="mt-1.5 text-[11px] text-fg-faint">{text}</div>
}

const TRIGGER_ICON = { task: AlarmClock, worker: BellRing, watch: Eye, scan: Radar } as const
const hhmm = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

/** A line no one typed: an automation woke the AI here (shown as a system line, not a bubble). */
function TriggerLine({ message }: { message: ChatMessage }) {
  const t = message.meta!.trigger!
  const Icon = TRIGGER_ICON[t.kind] ?? AlarmClock
  return (
    <div className="flex items-center gap-2 py-0.5 text-[11.5px] text-fg-subtle animate-rise">
      <span className="h-px flex-1 bg-[var(--line-soft)]" />
      <span className="inline-flex max-w-[80%] items-center gap-1.5 rounded-full border border-[var(--line-soft)] px-2.5 py-1">
        <Icon size={12} className="shrink-0" />
        <span className="truncate">{message.content}</span>
        <span className="num shrink-0 text-fg-faint">{hhmm(message.createdAt)}</span>
      </span>
      <span className="h-px flex-1 bg-[var(--line-soft)]" />
    </div>
  )
}

export function Message({ message, streaming }: { message: ChatMessage; streaming?: boolean }) {
  if (message.role === 'user' && message.meta?.trigger) return <TriggerLine message={message} />
  if (message.role === 'user') {
    return (
      <div className="flex justify-end animate-rise">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-tr-md border border-[var(--line-soft)] bg-[var(--color-surface-2)] px-3.5 py-2.5 text-[14px] text-fg shadow-[var(--e-1)]">
          {message.content}
        </div>
      </div>
    )
  }

  return (
    <div className="animate-rise">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="orb-mini" aria-hidden />
        <span className="text-[12px] font-medium text-fg-muted">Libera</span>
        {message.meta?.trigger && <span className="truncate text-[11px] text-fg-faint">· automated — {message.meta.trigger.title}</span>}
      </div>
      <div className="pl-[26px]">
        {/* The Grok-style activity timeline self-hides when there's nothing to show. */}
        <ActivityTimeline message={message} streaming={streaming} />
        {message.content && (
          <div className="relative mt-1">
            <Markdown content={message.content} />
            {streaming && <span className="caret" aria-hidden />}
          </div>
        )}
        {streaming && !message.content && (message.meta as { revising?: boolean } | undefined)?.revising && (
          <div className="lb-shimmer mt-1 text-[12.5px] text-fg-subtle">Revising the answer…</div>
        )}
        {!streaming && message.meta?.stopped && <div className="mt-1.5 text-[12px] text-fg-faint">Stopped — the answer is incomplete.</div>}
        <BillingNote message={message} streaming={streaming} />
        {message.citations && message.citations.length > 0 && (
          <Citations citations={message.citations} />
        )}
        {drafted(message, 'worker_draft', 'workerId').map((id) => (
          <WorkerCard key={id} id={id} />
        ))}
        {drafted(message, 'worker_group_draft', 'groupId').map((id) => (
          <WorkerGroupCard key={id} id={id} />
        ))}
        {drafted(message, 'order_draft', 'ticketId').map((id) => (
          <OrderCard key={id} id={id} />
        ))}
        {[...drafted(message, 'watch_create', 'watcherId'), ...drafted(message, 'scan_create', 'scanId')].map((id) => (
          <WatcherCard key={id} id={id} embedded />
        ))}
      </div>
    </div>
  )
}
